import React, { useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import { EditorContext } from "views/components/Editor/EditorContext";
import RotateHandleIcon from "./RotateHandleIcon";
import {
  calculateAngleFromMouse,
  normalizeAngle,
  getLowestSide,
  getMultiElementRotationAngle
} from "./utils";
import { rotateElementsAroundCenter } from "state/ui/editor/DesignUtils";
import style from "./style.module.css";

const RotateHandler = ({
  element,
  elements,
  highestSide,
  onRotationStart,
  onRotationEnd,
  onMultiElementRotationPreview,
  onMultiElementRotationCommit,
  multiElementRotationAngle = 0,
  // Group rotation props (for single group element)
  onGroupRotationPreview,
  groupRotationAngle = 0
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // Cache the side position when drag starts so it doesn't jump during rotation
  const [dragStartSide, setDragStartSide] = useState(null);
  // For multi-element mode, track the displayed angle
  const [multiElementDisplayAngle, setMultiElementDisplayAngle] = useState(0);
  const initialAngleRef = useRef(0);
  const initialElementsRef = useRef([]);
  // For multi-element mode, track the base cumulative angle at drag start
  const baseMultiElementAngleRef = useRef(0);
  const prevMouseAngleRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);
  const wrapperRef = useRef(null);

  const {
    onElementPreview,
    onElementAttributeChange,
    onElementsAttributesChange,
    updateElementPreviews,
    clearElementPreviewsOnly,
    selectedItems,
    isInTextEditMode
  } = useContext(EditorContext);

  // Keep refs to avoid stale closure issues in event handlers
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const onElementsAttributesChangeRef = useRef(onElementsAttributesChange);
  onElementsAttributesChangeRef.current = onElementsAttributesChange;

  const updateElementPreviewsRef = useRef(updateElementPreviews);
  updateElementPreviewsRef.current = updateElementPreviews;

  const clearElementPreviewsOnlyRef = useRef(clearElementPreviewsOnly);
  clearElementPreviewsOnlyRef.current = clearElementPreviewsOnly;

  // Determine if we're in multi-element mode
  const isMultiElementMode = elements && elements.length > 1;

  // Detect if element is a group
  const isGroupElement = element?.type === "group";

  // Get group children if applicable
  const groupChildren = isGroupElement ? element.getElements?.() : null;

  // Compute whether the handler should render (done after all hooks to satisfy Rules of Hooks)
  const shouldRender = (() => {
    if (isMultiElementMode) {
      // Multi-element mode: render only if ALL elements can rotate
      return elements.every(
        el => !el?.isAttributeRestricted?.("angle") && el?.type !== "table"
      );
    }

    // Single-element mode
    if (!element) return false;
    if (element?.type === "table") return false;

    // For groups, check child restrictions via getRestrictionsMap
    if (isGroupElement) {
      // Don't render if group has no children
      if (!groupChildren || groupChildren.length === 0) return false;
      // Don't render if any child is a table
      if (groupChildren.some(child => child?.type === "table")) return false;
      // Check aggregated restrictions from children
      const restrictionsMap = element.getRestrictionsMap?.() || {};
      if (restrictionsMap.angle) return false;
      return true;
    }

    // For non-groups, use standard restriction check
    if (element?.isAttributeRestricted?.("angle")) return false;

    return true;
  })();

  // Early return AFTER all hooks are declared
  if (!shouldRender) {
    return null;
  }

  // Determine if we should use multi-element rotation logic (for actual multi-selection OR group elements)
  const useMultiElementRotation = isMultiElementMode || isGroupElement;

  // The elements to rotate: for multi-selection use elements prop, for groups use children
  const elementsToRotate = isMultiElementMode ? elements : groupChildren;

  // Ensure angle is always a number (actionbar may pass strings)
  // For groups and multi-element mode, use the multiElementDisplayAngle
  const currentAngle = useMultiElementRotation
    ? multiElementDisplayAngle
    : Number(element?.angle) || 0;

  // Position on the opposite side from the context menu
  // For multi-element mode and groups, always position at bottom
  // Use cached side during drag to prevent jumping
  const lowestSide = useMultiElementRotation
    ? "bottom"
    : isDragging && dragStartSide
    ? dragStartSide
    : getLowestSide(highestSide);

  // Counter-rotate the icon and badge so they're always readable (like ElementContextMenu)
  // For multi-element mode and groups, counter-rotate by the current rotation angle
  // since the selection box is rotating and we want the handle to stay level
  const counterRotateTransform = (() => {
    if (isMultiElementMode) {
      return `rotate(${-multiElementRotationAngle}deg)`;
    }
    if (isGroupElement) {
      return `rotate(${-groupRotationAngle}deg)`;
    }
    return `rotate(-${normalizeAngle(Number(element?.angle) || 0)}deg)`;
  })();

  const getElementCenter = () => {
    // Get the ResizeBox wrapper (parent of our wrapper) to calculate center
    // This is the same approach used by ResizeHandler
    if (!wrapperRef.current) return null;

    const resizeBoxNode = wrapperRef.current.parentElement;
    if (!resizeBoxNode) return null;

    const rect = resizeBoxNode.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  const handleClick = e => {
    // Prevent click events from propagating and causing selection changes
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();

    const center = getElementCenter();
    if (!center) return;

    // Cache the current side so it doesn't change during rotation
    if (!useMultiElementRotation) {
      setDragStartSide(getLowestSide(highestSide));
    }

    // For multi-element mode or group elements, store the initial state of all elements
    if (useMultiElementRotation) {
      initialElementsRef.current = elementsToRotate.map(el => ({
        uniqueId: el.uniqueId,
        type: el.type,
        left: el.left,
        top: el.top,
        width: el.width,
        height: el.height,
        angle: Number(el.angle) || 0,
        // Line-specific properties for coordinate-based rotation
        ...(el.type === "line" && {
          coordinates: { ...el.coordinates },
          thickness: el.thickness,
          endcap: el.endcap
        })
      }));
      // Calculate and store the base cumulative angle (same logic as RotateButton)
      // This ensures consistency between the handle badge and actionbar display
      baseMultiElementAngleRef.current = getMultiElementRotationAngle(
        elementsToRotate
      );
      setMultiElementDisplayAngle(baseMultiElementAngleRef.current);
    } else {
      initialAngleRef.current = Number(element?.angle) || 0;
    }

    prevMouseAngleRef.current = calculateAngleFromMouse(
      e.clientX,
      e.clientY,
      center.x,
      center.y
    );
    accumulatedDeltaRef.current = 0;

    setIsDragging(true);
    onRotationStart?.();

    const handleMouseMove = moveEvent => {
      const currentCenter = getElementCenter();
      if (!currentCenter) return;

      const mouseAngle = calculateAngleFromMouse(
        moveEvent.clientX,
        moveEvent.clientY,
        currentCenter.x,
        currentCenter.y
      );

      // Calculate delta from previous position, handling wraparound
      let delta = mouseAngle - prevMouseAngleRef.current;
      // Handle wraparound: if delta > 180, we crossed 0/360 going counter-clockwise
      // if delta < -180, we crossed 0/360 going clockwise
      if (delta > 180) {
        delta -= 360;
      } else if (delta < -180) {
        delta += 360;
      }

      accumulatedDeltaRef.current += delta;
      prevMouseAngleRef.current = mouseAngle;

      if (useMultiElementRotation) {
        // Multi-element mode or group element: rotate all elements around their centroid
        // Display = base cumulative angle + current drag delta
        const displayAngle = normalizeAngle(
          baseMultiElementAngleRef.current + accumulatedDeltaRef.current
        );
        setMultiElementDisplayAngle(displayAngle);

        const rotatedElements = rotateElementsAroundCenter({
          elements: initialElementsRef.current,
          angle: accumulatedDeltaRef.current
        });

        if (isMultiElementMode) {
          // Multi-element selection: use passed-in callbacks
          if (typeof onMultiElementRotationPreview === "function") {
            onMultiElementRotationPreview(
              rotatedElements,
              accumulatedDeltaRef.current
            );
          }
        } else {
          // Group element: use updateElementPreviews which updates elementPreviews
          // without modifying selectedItems (preserves group selection)
          if (typeof updateElementPreviewsRef.current === "function") {
            updateElementPreviewsRef.current(rotatedElements);
          }
          // Also notify parent to rotate the selection box visually
          if (typeof onGroupRotationPreview === "function") {
            onGroupRotationPreview(accumulatedDeltaRef.current);
          }
        }
      } else {
        // Single-element mode: update the angle directly
        const newAngle = normalizeAngle(
          initialAngleRef.current + accumulatedDeltaRef.current
        );

        if (typeof onElementPreview === "function") {
          onElementPreview({
            angle: newAngle
          });
        }
      }
    };

    const handleMouseUp = upEvent => {
      upEvent.preventDefault();
      upEvent.stopPropagation();

      setIsDragging(false);
      setDragStartSide(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp, true);

      if (useMultiElementRotation) {
        // Multi-element mode or group element: commit the rotated elements
        const rotatedElements = rotateElementsAroundCenter({
          elements: initialElementsRef.current,
          angle: accumulatedDeltaRef.current
        });

        if (isMultiElementMode) {
          // Multi-element selection: use passed-in callback
          if (typeof onMultiElementRotationCommit === "function") {
            onMultiElementRotationCommit(rotatedElements);
          }
        } else {
          // Group element: use context callback to commit rotation
          // This updates designData directly without changing selectedItems
          if (typeof onElementsAttributesChangeRef.current === "function") {
            onElementsAttributesChangeRef.current(rotatedElements);
            // Clear element previews after committing so undo works correctly
            // Without this, the previews would persist and undo would appear to have no effect
            // Use clearElementPreviewsOnly to preserve selection (not onClearElementPreviews)
            if (typeof clearElementPreviewsOnlyRef.current === "function") {
              clearElementPreviewsOnlyRef.current();
            }
          } else {
            console.warn(
              "[RotateHandler] onElementsAttributesChange is not available"
            );
          }
        }
      } else {
        // Single-element mode: commit the final angle
        const finalAngle = normalizeAngle(
          initialAngleRef.current + accumulatedDeltaRef.current
        );

        if (typeof onElementAttributeChange === "function") {
          onElementAttributeChange(
            {
              angle: finalAngle
            },
            selectedItemsRef.current
          );
        }
      }

      onRotationEnd?.();

      // Prevent any click event that might fire after mouseup (would cause deselection)
      const preventClick = clickEvent => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        document.removeEventListener("click", preventClick, true);
      };
      document.addEventListener("click", preventClick, true);
      setTimeout(() => {
        document.removeEventListener("click", preventClick, true);
      }, 100);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp, true);
  };

  return (
    <div
      ref={wrapperRef}
      className={style.rotateHandleWrapper}
      data-is-editing={isInTextEditMode}
      data-side={lowestSide}
      onMouseDown={handleClick}
      onClick={handleClick}
    >
      {/* Inner container is counter-rotated so flex layout stays screen-aligned */}
      {/* For multi-element mode, counter-rotate by group rotation angle to keep handle level */}
      <div
        className={style.rotateHandleInner}
        style={{ transform: counterRotateTransform }}
      >
        <div
          className={style.rotateHandle}
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <RotateHandleIcon size={18} />
        </div>
        {isDragging && (
          <div className={style.angleBadge}>{Math.round(currentAngle)}°</div>
        )}
      </div>
    </div>
  );
};

RotateHandler.propTypes = {
  element: PropTypes.object,
  elements: PropTypes.array,
  highestSide: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  onRotationStart: PropTypes.func,
  onRotationEnd: PropTypes.func,
  onMultiElementRotationPreview: PropTypes.func,
  onMultiElementRotationCommit: PropTypes.func,
  multiElementRotationAngle: PropTypes.number,
  onGroupRotationPreview: PropTypes.func,
  groupRotationAngle: PropTypes.number
};

export default RotateHandler;
